import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { DatabaseUser } from '@/types/compatibility'

export type TabType = 'users' | 'roles' | 'privileges'

interface DatabaseUsersSelection {
  selectedDatabase: string | null
  selectedUser: DatabaseUser | null
  selectedUsers: Set<string> // Set of user keys (username@host)
  lastClickedUser: string | null // For shift-click range selection
  activeTab: TabType // Current active tab
}

interface DatabaseUsersSelectionState {
  // State keyed by connectionId
  selections: Record<string, DatabaseUsersSelection>
  
  // Actions
  setSelectedDatabase: (connectionId: string, database: string | null) => void
  setSelectedUser: (connectionId: string, user: DatabaseUser | null) => void
  setSelectedUsers: (connectionId: string, users: Set<string>) => void
  addSelectedUser: (connectionId: string, userKey: string) => void
  removeSelectedUser: (connectionId: string, userKey: string) => void
  toggleSelectedUser: (connectionId: string, userKey: string) => void
  setLastClickedUser: (connectionId: string, userKey: string | null) => void
  setActiveTab: (connectionId: string, tab: TabType) => void
  clearSelectedUsers: (connectionId: string) => void
  getSelection: (connectionId: string) => DatabaseUsersSelection
  clearSelectionForConnection: (connectionId: string) => void
  clearAllSelections: () => void
}

const defaultSelection: DatabaseUsersSelection = {
  selectedDatabase: null,
  selectedUser: null,
  selectedUsers: new Set<string>(),
  lastClickedUser: null,
  activeTab: 'users',
}

export const useDatabaseUsersSelectionStore = create<DatabaseUsersSelectionState>()(
  persist(
    (set, get) => ({
      selections: {},

      setSelectedDatabase: (connectionId, database) => {
        set((state) => ({
          selections: {
            ...state.selections,
            [connectionId]: {
              ...state.selections[connectionId],
              selectedDatabase: database,
              // Clear selected users when database changes
              selectedUser: null,
              selectedUsers: new Set<string>(),
              lastClickedUser: null,
            }
          }
        }))
      },

      setSelectedUser: (connectionId, user) => {
        set((state) => ({
          selections: {
            ...state.selections,
            [connectionId]: {
              ...state.selections[connectionId],
              selectedUser: user,
            }
          }
        }))
      },

      setSelectedUsers: (connectionId, users) => {
        set((state) => ({
          selections: {
            ...state.selections,
            [connectionId]: {
              ...state.selections[connectionId],
              selectedUsers: users,
            }
          }
        }))
      },

      addSelectedUser: (connectionId, userKey) => {
        set((state) => {
          const current = state.selections[connectionId] || defaultSelection
          const newSelectedUsers = new Set(current.selectedUsers)
          newSelectedUsers.add(userKey)
          return {
            selections: {
              ...state.selections,
              [connectionId]: {
                ...current,
                selectedUsers: newSelectedUsers,
              }
            }
          }
        })
      },

      removeSelectedUser: (connectionId, userKey) => {
        set((state) => {
          const current = state.selections[connectionId] || defaultSelection
          const newSelectedUsers = new Set(current.selectedUsers)
          newSelectedUsers.delete(userKey)
          return {
            selections: {
              ...state.selections,
              [connectionId]: {
                ...current,
                selectedUsers: newSelectedUsers,
              }
            }
          }
        })
      },

      toggleSelectedUser: (connectionId, userKey) => {
        set((state) => {
          const current = state.selections[connectionId] || defaultSelection
          const newSelectedUsers = new Set(current.selectedUsers)
          if (newSelectedUsers.has(userKey)) {
            newSelectedUsers.delete(userKey)
          } else {
            newSelectedUsers.add(userKey)
          }
          return {
            selections: {
              ...state.selections,
              [connectionId]: {
                ...current,
                selectedUsers: newSelectedUsers,
              }
            }
          }
        })
      },

      setLastClickedUser: (connectionId, userKey) => {
        set((state) => ({
          selections: {
            ...state.selections,
            [connectionId]: {
              ...state.selections[connectionId],
              lastClickedUser: userKey,
            }
          }
        }))
      },

      setActiveTab: (connectionId, tab) => {
        set((state) => ({
          selections: {
            ...state.selections,
            [connectionId]: {
              ...state.selections[connectionId],
              activeTab: tab,
            }
          }
        }))
      },

      clearSelectedUsers: (connectionId) => {
        set((state) => ({
          selections: {
            ...state.selections,
            [connectionId]: {
              ...state.selections[connectionId],
              selectedUsers: new Set<string>(),
              lastClickedUser: null,
            }
          }
        }))
      },

      getSelection: (connectionId) => {
        const state = get()
        return state.selections[connectionId] || defaultSelection
      },

      clearSelectionForConnection: (connectionId) => {
        set((state) => {
          const { [connectionId]: _, ...rest } = state.selections
          return { selections: rest }
        })
      },

      clearAllSelections: () => {
        set({ selections: {} })
      },
    }),
    {
      name: 'database-users-selection-storage',
      storage: createJSONStorage(() => localStorage),
      // Custom serialization to handle Set objects
      partialize: (state) => ({
        selections: Object.fromEntries(
          Object.entries(state.selections).map(([key, value]) => [
            key,
            {
              ...value,
              selectedUsers: Array.from(value.selectedUsers || []),
            }
          ])
        )
      }),
      // Custom deserialization to restore Set objects
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.selections = Object.fromEntries(
            Object.entries(state.selections).map(([key, value]) => {
              const valUnknown: unknown = value
              const hasObject = valUnknown !== null && typeof valUnknown === 'object'
              const candidate = hasObject ? valUnknown as Partial<DatabaseUsersSelection> & { selectedUsers?: string[] } : {}
              const val: DatabaseUsersSelection = {
                selectedDatabase: candidate.selectedDatabase ?? null,
                selectedUser: candidate.selectedUser ?? null,
                selectedUsers: new Set(candidate.selectedUsers ?? []),
                lastClickedUser: candidate.lastClickedUser ?? null,
                activeTab: candidate.activeTab ?? 'users',
              }
              return [
                key,
                val
              ]
            })
          )
        }
      },
    }
  )
)